import { Box } from '@mui/material';
import { Map, WildFireGeoEventMarker } from 'features/mapping/components/Map';
import { useMapState } from 'state/useMapState';
import { useCallback, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import { MAP_ENTITY_DRAWER_MIN_SNAP_PCT } from 'features/mapping/components/MapEntityDrawer';
import { useContextMenu } from 'hooks/useContextMenu';
import { MapToolsContextMenu } from 'features/mapping/components/MapToolsContextMenu';
import { LatLng } from 'shared/types';
import { useMapLongPress } from 'hooks/useMapLongPress';
import { useAuthState } from 'state/useAuthState';
import { ClearTimeoutMap } from 'components/IncidentsMap/ClearTimeoutMap';
import { LockedMapPinActiveProvider } from 'features/mapping/mapPin';
import { MapCenterWithPinMarker } from 'components/IncidentsMap/MapCenterWithPinMarker';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { CenterMap } from 'features/mapping/components/CenterMap';
import { StructuredEvacuationsViewLayer } from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import { useMapLayersState } from 'state/useMapLayersState';
import {
  ElectricLinesLayer,
  GasPipelineLayer,
} from 'features/mapping/components/layers/TransmissionLinesLayer';
import { PrivateLandOwnershipLayer } from 'features/mapping/components/layers/PrivateLandOwnershipLayer';
import { MapLayers } from 'features/mapping/mapping.constants';
import { hasValidCoordinates } from 'shared/utils';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { MapType } from 'features/mapping/mapTypePolicy';
import { AllExternalFeatureLayers } from 'features/mapping/components/layers/ExternalFeatureLayer';
import { ProgressionModelLayer } from '../ProgressionModelLayer';

type ProgressionModelMapProps = {
  geoEventId: number;
  modelId: number;
};

export const ProgressionModelMap = (
  props: ProgressionModelMapProps,
): JSX.Element => {
  const { geoEventId, modelId } = props;
  const [mapLongPressCoordinates, setMapLongPressCoordinates] =
    useState<LatLng | null>(null);
  const { incidentMapState, updateIncidentMapState } = useMapState();
  const { showMembershipProFeatures } = useAuthState();
  const { ctxMenuClose, contextMenuCoordinates, openContextMenu } =
    useContextMenu();
  const { geoEvent } = useGeoEvent<Wildfire>({
    id: geoEventId,
    useCacheBuster: true,
  });
  const { mapLayers } = useMapLayersState();
  const { minimizeEntityDrawer } = useMapEntityDrawer();

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      updateIncidentMapState(viewport);
    },
    [updateIncidentMapState],
  );

  const handleMapLongPress = useCallback(
    (coordinates: LatLng): void => {
      if (!showMembershipProFeatures) {
        return;
      }
      setMapLongPressCoordinates(coordinates);
    },
    [showMembershipProFeatures],
  );

  const { onTouchStart, clearMobileTimeout } = useMapLongPress({
    onLongPress: handleMapLongPress,
  });

  const center = useMemo(
    () => ({
      lat: incidentMapState.center.lat,
      lng: incidentMapState.center.lng,
    }),
    [incidentMapState.center.lat, incidentMapState.center.lng],
  );

  const geoEventLatLng = useMemo(
    (): LatLng | null =>
      geoEvent && hasValidCoordinates(geoEvent)
        ? { lat: geoEvent.lat, lng: geoEvent.lng }
        : null,
    [geoEvent],
  );

  const isMobileApp = Capacitor.isNativePlatform();

  return (
    <LockedMapPinActiveProvider>
      <Box sx={{ flex: 1, display: 'flex' }}>
        <Box
          sx={{ flexGrow: 1, position: 'relative' }}
          onMouseDown={isMobileApp ? undefined : () => minimizeEntityDrawer()}
          onTouchStart={isMobileApp ? () => minimizeEntityDrawer() : undefined}
          onContextMenu={(e) => {
            // It's been added to prevent opening native context menu when MUI menu was open.
            e.preventDefault();
          }}
        >
          <Map
            mapType={MapType.Main}
            center={center}
            zoom={incidentMapState.zoom}
            onContextMenu={openContextMenu}
            onTouchStart={onTouchStart}
          >
            <ProgressionModelLayer visible modelId={modelId} />
            <AllExternalFeatureLayers interactive={false} />
            <StructuredEvacuationsViewLayer />
            <GasPipelineLayer
              visible={mapLayers.includes(MapLayers.GAS_PIPELINES)}
            />
            <ElectricLinesLayer
              visible={mapLayers.includes(MapLayers.ELECTRICAL_LINES)}
            />
            <PrivateLandOwnershipLayer
              visible={mapLayers.includes(MapLayers.PRIVATE_LAND_OWNERSHIP)}
            />
            <Map.MapEvents onViewportChange={onViewportChange} />
            <MapCenterWithPinMarker />
            <ClearTimeoutMap clearMobileTimeout={clearMobileTimeout} />
            <MapToolsContextMenu
              // This instance is for the "right-click" context menu.
              variant="menu"
              onClose={ctxMenuClose}
              pointCoordinates={contextMenuCoordinates}
              anchorPosition={
                (contextMenuCoordinates && {
                  top: contextMenuCoordinates.mouseY,
                  left: contextMenuCoordinates.mouseX,
                }) || { top: 0, left: 0 }
              }
            />
            <MapToolsContextMenu
              // This instance is for map long-presses.
              variant="drawer"
              onClose={() => {
                setMapLongPressCoordinates(null);
              }}
              pointCoordinates={mapLongPressCoordinates}
            />
            {geoEvent && <WildFireGeoEventMarker geoEvent={geoEvent} />}
            <CenterMap
              zoomIn
              latLng={geoEventLatLng}
              drawerIsOpen
              defaultSnapPointPct={MAP_ENTITY_DRAWER_MIN_SNAP_PCT}
            />
          </Map>
        </Box>
      </Box>
    </LockedMapPinActiveProvider>
  );
};
